import { similarCompanies, similarPeople } from '@ly360/core';
import { Dialog, ErrorState, Select } from '@ly360/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useAccountOptions, usePeopleOptions } from './data';
import { NewPersonDialog } from './forms';

interface SimilarCompany {
  id: string;
  name: string;
  nameAr: string | null;
  city: string | null;
  reasons: string[];
}
interface SimilarPerson {
  id: string;
  name: string;
  company: string | null;
  phones: string[];
  emails: string[];
  reasons: string[];
}

/** Waits until the value has been stable for `ms` (to avoid a request per keystroke). */
function useDebounced<T>(value: T, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), ms);
    return () => clearTimeout(h);
  }, [value, ms]);
  return v;
}

/**
 * Company field: search existing companies (with "did you mean …?" for typos), or create one right here. Before
 * creating, near-matches are offered first so the same company is not added twice.
 */
export function AccountPicker({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useAccountOptions();
  const [check, setCheck] = useState<{ name: string; matches: SimilarCompany[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const items = q.data?.items ?? [];
  const label = (a: { name: string; nameAr: string | null }) =>
    i18n.language === 'ar' && a.nameAr ? a.nameAr : a.name;

  const create = async (name: string) => {
    setError(null);
    try {
      const r = await api<{ id: string }>('/v1/accounts', { method: 'POST', body: JSON.stringify({ name }) });
      await qc.invalidateQueries({ queryKey: ['account-options'] });
      onChange(r.id);
      setCheck(null);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <>
      <Select
        id={id}
        value={value}
        onChange={onChange}
        searchable
        placeholder={t('common.none')}
        options={[
          { value: '', label: t('common.none') },
          ...items.map((a) => ({ value: a.id, label: label(a) })),
        ]}
        suggest={(text) =>
          similarCompanies(text, items).map((s) => {
            const a = items.find((x) => x.id === s.id)!;
            return { value: a.id, label: label(a) };
          })
        }
        create={{
          label: (text) => t('crm.create.company', { name: text }),
          onCreate: async (name) => {
            const r = await api<{ items: SimilarCompany[] }>(
              `/v1/accounts/similar?name=${encodeURIComponent(name)}`,
            ).catch(() => ({ items: [] as SimilarCompany[] }));
            if (r.items.length) setCheck({ name, matches: r.items });
            else await create(name);
          },
        }}
      />
      {!!error && <ErrorState error={error} />}
      {check && (
        <Dialog
          open
          title={t('crm.create.similarTitle')}
          onClose={() => setCheck(null)}
          testId="similar-company"
        >
          <p style={{ marginTop: 0 }}>{t('crm.create.similarCompanies', { name: check.name })}</p>
          <ul className="list-plain">
            {check.matches.map((m) => (
              <li key={m.id} className="similar-row">
                <span className="grow" dir="auto">
                  <strong>{label(m)}</strong>
                  {m.city && <span className="faint"> · {m.city}</span>}
                  <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                    {m.reasons.map((r) => t(`crm.create.reason.${r}`)).join(' · ')}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-grad btn-sm"
                  onClick={() => {
                    onChange(m.id);
                    setCheck(null);
                  }}
                >
                  {t('crm.create.useThis')}
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void create(check.name)}
            data-testid="create-anyway"
          >
            {t('crm.create.createAnyway', { name: check.name })}
          </button>
        </Dialog>
      )}
    </>
  );
}

/**
 * Contact field: search existing people (similar names in either script are suggested), or create one here; the
 * New person form opens on top, pre-filled, with its own duplicate checks.
 */
export function PersonPicker({
  id,
  value,
  onChange,
  accountId,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  /** Pre-fills the company when creating a person from here. */
  accountId?: string;
}) {
  const { t } = useTranslation();
  const q = usePeopleOptions();
  const [creating, setCreating] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  return (
    <>
      <Select
        id={id}
        value={value}
        onChange={onChange}
        searchable
        placeholder={t('common.none')}
        options={[
          { value: '', label: t('common.none') },
          ...items.map((p) => ({ value: p.id, label: p.displayName, hint: p.accountName ?? undefined })),
        ]}
        suggest={(text) =>
          similarPeople(
            { name: text },
            items.map((p) => ({
              id: p.id,
              name: p.displayName,
              nameArabic: p.nameArabic,
              emails: [],
              phones: [],
              accountIds: [],
            })),
          ).map((s) => ({ value: s.id, label: items.find((p) => p.id === s.id)!.displayName }))
        }
        create={{
          label: (text) => t('crm.create.person', { name: text }),
          onCreate: (name) => setCreating(name),
        }}
      />
      {/* Portalled: a form opened from inside another form must not be nested in it. */}
      {creating !== null &&
        createPortal(
          <NewPersonDialog
            open
            initialName={creating}
            accountId={accountId}
            onClose={() => setCreating(null)}
            onCreated={(newId) => {
              onChange(newId);
              setCreating(null);
            }}
          />,
          document.body,
        )}
    </>
  );
}

/** Live "possible existing records" while filling in a new person. */
export function SimilarPeopleHint({
  name,
  phone,
  email,
  onUse,
}: {
  name: string;
  phone: string;
  email: string;
  /** When given, "Use this person" picks the existing record instead of creating a new one. */
  onUse?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const key = useDebounced({ name: name.trim(), phone: phone.trim(), email: email.trim() });
  const enabled = key.name.length >= 3 || key.phone.length >= 7 || key.email.includes('@');
  const q = useQuery({
    queryKey: ['similar-people', key],
    enabled,
    queryFn: () =>
      api<{ items: SimilarPerson[] }>(
        `/v1/people/similar?${new URLSearchParams({ name: key.name, ...(key.phone ? { phone: key.phone } : {}), ...(key.email ? { email: key.email } : {}) })}`,
      ),
  });
  const items = enabled ? (q.data?.items ?? []) : [];
  if (!items.length) return null;
  const certain = items.some((i) => i.reasons.includes('same_phone') || i.reasons.includes('same_email'));
  return (
    <div className="similar-box" role="status" data-testid="similar-people">
      <strong className="title">
        {certain ? t('crm.create.alreadyExists') : t('crm.create.mightExist')}
      </strong>
      {items.map((p) => (
        <div key={p.id} className="similar-row">
          <span className="grow" dir="auto">
            <strong>{p.name}</strong>
            {p.company && <span className="faint"> · {p.company}</span>}
            <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }} dir="ltr">
              {[...p.phones, ...p.emails].slice(0, 2).join(' · ')}
            </span>
            <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
              {p.reasons.map((r) => t(`crm.create.reason.${r}`)).join(' · ')}
            </span>
          </span>
          {onUse ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onUse(p.id)}
              data-testid="use-existing"
            >
              {t('crm.create.useThisPerson')}
            </button>
          ) : (
            <Link className="btn btn-sm" to={`/people/${p.id}`}>
              {t('crm.create.open')}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

/** Live "possible existing companies" while filling in a new company. */
export function SimilarCompaniesHint({ name }: { name: string }) {
  const { t, i18n } = useTranslation();
  const key = useDebounced(name.trim());
  const q = useQuery({
    queryKey: ['similar-companies', key],
    enabled: key.length >= 3,
    queryFn: () => api<{ items: SimilarCompany[] }>(`/v1/accounts/similar?name=${encodeURIComponent(key)}`),
  });
  const items = key.length >= 3 ? (q.data?.items ?? []) : [];
  if (!items.length) return null;
  return (
    <div className="similar-box" role="status" data-testid="similar-companies">
      <strong className="title">{t('crm.create.mightExistCompany')}</strong>
      {items.map((c) => (
        <div key={c.id} className="similar-row">
          <span className="grow" dir="auto">
            <strong>{i18n.language === 'ar' && c.nameAr ? c.nameAr : c.name}</strong>
            {c.city && <span className="faint"> · {c.city}</span>}
            <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
              {c.reasons.map((r) => t(`crm.create.reason.${r}`)).join(' · ')}
            </span>
          </span>
          <Link className="btn btn-sm" to={`/companies/${c.id}`}>
            {t('crm.create.open')}
          </Link>
        </div>
      ))}
    </div>
  );
}
